import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AnimatedSection from './AnimatedSection';

function FAQ() {
  const [expanded, setExpanded] = useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const faqs = [
    {
      category: 'General',
      questions: [
        {
          question: 'What is Lagos Impact Hackathon?',
          answer: 'Lagos Impact Hackathon is Nigeria\'s most prestigious innovation summit, held in partnership with UNDP. It\'s a 48-hour venture builder bringing together 150 elite, pre-vetted talents to build solutions for real-world challenges aligned with Sustainable Development Goals.'
        },
        {
          question: 'When and where is the event?',
          answer: 'The hackathon will take place December 11-13, 2025, at the University of Lagos Guest Houses and Conference Centre, Akoka, Lagos, Nigeria.'
        },
        {
          question: 'Who can participate?',
          answer: 'We are looking for Nigeria\'s most elite talent - developers, designers, marketers, and innovators with proven expertise. All applicants go through a rigorous selection process.'
        }
      ]
    },
    {
      category: 'Registration',
      questions: [
        {
          question: 'How do I register?',
          answer: 'Fill out the registration form on our website. You\'ll need to provide your track preference, relevant experience, skills, and motivation. Only the most qualified candidates will be selected.'
        },
        {
          question: 'Is there a registration fee?',
          answer: 'No, participation is completely free for selected candidates. We cover meals, accommodation, and provide all necessary resources during the event.'
        },
        {
          question: 'When is the registration deadline?',
          answer: 'Registration closes on December 4, 2025. However, we encourage early applications as spots are limited and selection is highly competitive.'
        }
      ]
    },
    {
      category: 'Tracks & Teams',
      questions: [
        {
          question: 'What are the two tracks?',
          answer: 'We have two parallel tracks: Tech for Impact (building functional open-source solutions) and Marketing for Impact (developing world-class communication strategies for NGOs). Each track addresses critical challenges in unique ways.'
        },
        {
          question: 'Can I participate as an individual or do I need a team?',
          answer: 'For the Tech track, you can participate as an individual (we\'ll match you with a team) or come with a pre-formed team of 2-4 members. Marketing track participants will also be organized into teams based on skills and experience.'
        },
        {
          question: 'Can I switch tracks after registration?',
          answer: 'Track changes after registration are not typically allowed as we carefully curate teams based on initial selections. Please choose your track carefully when applying.'
        }
      ]
    },
    {
      category: 'Prizes & Awards',
      questions: [
        {
          question: 'What prizes are available?',
          answer: 'Winners receive cash prizes, mentorship opportunities, access to funding networks, and potential partnerships with UNDP and other leading organizations. Specific prize amounts will be announced closer to the event.'
        },
        {
          question: 'How are winners selected?',
          answer: 'Projects are judged based on innovation, feasibility, impact potential, alignment with SDGs, and presentation quality. A panel of expert judges from tech, business, and development sectors will evaluate all submissions.'
        }
      ]
    },
    {
      category: 'Event Details',
      questions: [
        {
          question: 'What should I bring to the hackathon?',
          answer: 'Bring your laptop, chargers, any necessary development tools, and your creativity! We provide meals, snacks, workspace, internet, and accommodation. A detailed packing list will be sent to accepted participants.'
        },
        {
          question: 'Will there be mentors available?',
          answer: 'Yes! We\'ll have experienced mentors from leading tech companies, NGOs, and development organizations available throughout the event to guide teams and answer questions.'
        },
        {
          question: 'Is accommodation provided?',
          answer: 'Yes, we provide accommodation for all accepted participants for the duration of the hackathon. Details will be shared with selected candidates.'
        }
      ]
    }
  ];

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      <AnimatedSection animation="fadeIn">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <HelpOutlineIcon sx={{ fontSize: 40, color: 'primary.main', mr: 1 }} />
            <Typography variant="h3" component="h2" sx={{ fontWeight: 'bold' }}>
              Frequently Asked Questions
            </Typography>
          </Box>
          <Typography variant="h6" color="text.secondary">
            Find answers to common questions about the hackathon
          </Typography>
        </Box>
      </AnimatedSection>

      {faqs.map((section, sectionIndex) => (
        <AnimatedSection key={sectionIndex} animation="slideUp" delay={sectionIndex * 0.1}>
          <Box sx={{ mb: 4 }}>
            <Chip
              label={section.category}
              sx={(theme) => ({
                mb: 2,
                fontWeight: 'bold',
                fontSize: '0.9rem',
                bgcolor: 'primary.main',
                color: theme.palette.getContrastText(theme.palette.primary.main)
              })}
            />
            {section.questions.map((faq, index) => (
              <Accordion
                key={index}
                expanded={expanded === `${sectionIndex}-${index}`}
                onChange={handleChange(`${sectionIndex}-${index}`)}
                sx={{
                  mb: 1,
                  '&:before': { display: 'none' },
                  boxShadow: expanded === `${sectionIndex}-${index}` ? 3 : 1,
                  borderRadius: '8px !important',
                  '&.Mui-expanded': {
                    margin: '0 0 8px 0'
                  }
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={(theme) => ({
                    '&.Mui-expanded': {
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(0, 100, 0, 0.25)' : 'primary.light',
                      color: theme.palette.mode === 'dark' ? 'text.primary' : 'primary.contrastText'
                    },
                    borderRadius: '8px'
                  })}
                >
                  <Typography sx={{ fontWeight: 600 }}>
                    {faq.question}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 2 }}>
                  <Typography color="text.secondary">
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </AnimatedSection>
      ))}

      <AnimatedSection animation="fadeIn" delay={0.3}>
        <Box sx={(theme) => ({
          mt: 6,
          p: 4,
          textAlign: 'center',
          bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'grey.50',
          borderRadius: 2,
          border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none'
        })}>
          <Typography variant="h6" gutterBottom>
            Still have questions?
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            Can't find what you're looking for? Reach out to us directly.
          </Typography>
          <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
            📧 hello@lagosimpacthackathon.org
          </Typography>
        </Box>
      </AnimatedSection>
    </Container>
  );
}

export default FAQ;
